import React, { useState, useRef, useEffect } from 'react';

function TabKafka({ name, addQuoteItem, formatPrice, plansDatabases, closeModal }) {
  const [quantity, setQuantity] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [scalableStorage, setScalableStorage] = useState('');
  const [totalPrice, setTotalPrice] = useState(0);
  const searchInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const [brokers, setBrokers] = useState(3); // Set default to 3 Brokers


	// Define price per GiB for Scalable Storage
	const pricePerGiB = 0.2;

  // Focus and select the quantity input when a plan is selected
  useEffect(() => {
    if (selectedPlan) {
      quantityInputRef.current.focus();
      quantityInputRef.current.select();
    }
  }, [selectedPlan]);

  // Filter plans based on the search term for Kafka
  const filteredPlans = plansDatabases.filter(plan => {
    if (!plan.kafkaNodes) {
      return false;
    }
    // Create a regex from the search term, escaping special characters to prevent regex errors
    const regex = new RegExp(searchTerm.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'), 'i');
    return (
      regex.test(plan.Description) ||
      regex.test(plan.Slug) ||
      regex.test(`${plan.VCPUs} vCPU`) ||
      regex.test(`${plan.Memory} GiB Memory`) ||
      regex.test(`${plan.kafkaStorageMin} GiB Disk`) ||
      regex.test(plan.kafkaNVMe) ||
      regex.test(`${plan.Network} Gbps Network`) ||
      regex.test(`$${plan.kafkaPriceMin} /mo`)
    );
  });

  // Handle plan selection for Kafka
  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
    setScalableStorage(plan.kafkaStorageMin); // Set scalableStorage when a plan is selected
    setShowDropdown(false);
    setSearchTerm('');
    setBrokers(3); // Set brokers to 3 when plan is selected or changed
    calculateTotalPrice(plan.kafkaStorageMin, plan); // Calculate price immediately when plan is selected with the default set of brokers
  };

  const calculateTotalPrice = (storage, plan, brokers) => {
    if (!plan) return 0;

    // Check if brokers is one of the allowed values, otherwise default to 3
    if (![3, 6, 9, 15].includes(brokers)) {
        brokers = 3;
    }

    let basePrice = parseFloat(plan.kafkaPriceMin / 3 * brokers);
    let additionalStorageCost = (storage - plan.kafkaStorageMin / 3 * brokers) * pricePerGiB;
    let totalPrice = basePrice + additionalStorageCost;

    setTotalPrice(formatPrice(totalPrice));
};

	// Function to increment the storage, capped at the maximum limit
	const handleStorageIncrement = () => {
		const currentStorage = parseInt(scalableStorage, 10) || selectedPlan.kafkaStorageMin /3 * brokers;
		const newStorage = Math.min(selectedPlan.kafkaStorageMax /3 * brokers, currentStorage + 10 * brokers);
		setScalableStorage(newStorage); // Update the storage size in the state
		calculateTotalPrice(newStorage, selectedPlan, brokers); // Recalculate the total price with the new storage size
	};

	// Function to decrement the storage size, capped at the minimum limit
	const handleStorageDecrement = () => {
		const currentStorage = parseInt(scalableStorage, 10) || selectedPlan.kafkaStorageMin /3 * brokers;
		const newStorage = Math.max(selectedPlan.kafkaStorageMin /3 * brokers, currentStorage - 10 * brokers);
		setScalableStorage(newStorage); // Update the storage size in the state
		calculateTotalPrice(newStorage, selectedPlan, brokers); // Recalculate the total price with the new storage size
	};

	// Handle quantity change for Kafka
	const handleQuantityChange = (e) => {
		const value = e.target.value;
		if (value === '' || (Number(value) >= 1 && Number(value) <= 1000)) {
			setQuantity(value);
		}
	};

	// Handle quantity blur for Kafka
	const handleQuantityBlur = () => {
		if (quantity === '' || quantity < 1) {
			setQuantity(1);
			quantityInputRef.current.focus();
			quantityInputRef.current.select();
		}
	};

  // Handle adding the selected Kafka plan to the quote
  const handleAdd = () => {
    if (selectedPlan) {
			let description = `${selectedPlan.Description} - ${selectedPlan.VCPUs * brokers} vCPU / ${selectedPlan.Memory * brokers} GiB Memory / ${scalableStorage} GiB ${scalableStorage > selectedPlan.kafkaStorageMin /3 * brokers ? 'scalable storage' : (selectedPlan.kafkaNVMe === 'nvme' ? 'NVMe local storage' : 'SSD local storage')}.`;
      let features = `Cluster with ${brokers} Kafka brokers.`;

      addQuoteItem({
        serviceType: 'Databases - Kafka',
        name: name,
        description: description,
        features: features,
        transfer: '',
        priceMonthly: totalPrice,
        quantity: quantity || 1,
        totalPrice: Number(totalPrice.toString().replace(/,/g, '')) * (quantity || 1)
    });
      closeModal(); // Close the modal after adding the quote item
    }
  };

  return (
    <div id="modal-body" className="modal-body">
      <h5>SELECT DATABASE PLAN</h5>
      <div id="search-container" className="search-container">
        <input
          ref={searchInputRef}
          type="text"
          className="input"
          placeholder="Search Kafka plans or click to view all"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
          }}
          onClick={() => setShowDropdown(!showDropdown)}
        />
        {showDropdown && filteredPlans.length > 0 && (
          <ul id="dropdown" className="dropdown">
            {filteredPlans.map((plan, index) => (
              <li key={index} onClick={() => handlePlanSelect(plan)} className="flex font-darkGray">
                <div className="dropdown-plans-description">
                  {plan.Description}
                </div>
                <div className="dropdown-plans-specifications">
                  {plan.VCPUs} vCPU, {plan.Memory} GiB Memory, {plan.kafkaStorageMin} GiB Disk
                </div>
                <div id="price" className="price">
                  <span className="dollar">$</span>
                  <span>{formatPrice(parseInt(plan.kafkaPriceMin, 10))}</span>
                  <span className="period">/mo</span>
                </div>
              </li>
            ))}
          </ul>
        )}
        {showDropdown && filteredPlans.length === 0 && (
          <ul id="dropdown" className="dropdown">
            <li>No plans found</li>
          </ul>
        )}
      </div>
      {selectedPlan && (
        <div>
          <div id="card" className="card">
            <div id="card-header" className="card-header flex flex-between flex-center">
              <div>
                <h5>SELECTED PLAN</h5>
              </div>
              <div id="price" className="price">
                <span className="dollar">$</span>
                <span className="cost">{formatPrice(totalPrice)}</span>
                <span className="period">/mo</span>
              </div>
            </div>
            <div id="card-body" className="card-body">
              <p>{selectedPlan.Description}</p>
              <p>{selectedPlan.VCPUs * brokers} vCPU / {selectedPlan.Memory * brokers} GiB Memory / {scalableStorage} GiB Disk</p>
            </div>
          </div>
          {selectedPlan.kafkaNodes === "[3,6,9,15]" && (
            <>
              <h5>SELECT NUMBER OF BROKERS</h5>
              <div className="flex gap-dot5">
                <div
                  className={`radio-button ${brokers === 3 ? 'radio-button-active' : ''}`}
                  onClick={() => { 
                    setBrokers(3); 
                    setScalableStorage(selectedPlan.kafkaStorageMin)
                    setTotalPrice(selectedPlan.kafkaPriceMin)
                    }
                  }         
                >
                  <span>3 brokers</span>
                </div>
                <div
                  className={`radio-button ${brokers === 6 ? 'radio-button-active' : ''}`}
                  onClick={() => { 
                    setBrokers(6); 
                    setScalableStorage(selectedPlan.kafkaStorageMin * 2)
                    setTotalPrice(selectedPlan.kafkaPriceMin * 2)
                    }
                  }      
                >
                  <span>6 brokers</span>
                </div>
                <div
                  className={`radio-button ${brokers === 9 ? 'radio-button-active' : ''}`}
                  onClick={() => { 
                    setBrokers(9); 
                    setScalableStorage(selectedPlan.kafkaStorageMin * 3)
                    setTotalPrice(selectedPlan.kafkaPriceMin * 3)
                    }
                  } 
                >
                  <span>9 brokers</span>
                </div>
                <div
                  className={`radio-button ${brokers === 15 ? 'radio-button-active' : ''}`}
                  onClick={() => { 
                    setBrokers(15); 
                    setScalableStorage(selectedPlan.kafkaStorageMin * 5)
                    setTotalPrice(selectedPlan.kafkaPriceMin * 5)
                    }
                  } 
                >
                  <span>15 brokers</span>
                </div>
              </div>
            </>
          )}
					<h5>SELECT SCALABLE STORAGE SIZE</h5>
          <div className="flex flex-start flex-center gap-2">
						<div className="stepper-container">
							<div className="stepper-input-wrapper">
									<div className="stepper-input">
											<input 
											type="number" 
											className="stepper-input-field" 
											name="storage-count" 
											placeholder="" 
											min={selectedPlan ? selectedPlan.kafkaStorageMin /3 * brokers : undefined}
											max={selectedPlan ? selectedPlan.kafkaStorageMax /3 * brokers : undefined} 
											aria-invalid="false" 
											autoComplete="off" 
											spellCheck="false" 
											value={scalableStorage} // Bind the input to state
											onChange={(e) => {
													const value = e.target.value;

													// Allow the field to be empty
													if (value === '') {
													setScalableStorage('');
													return;
													}

													const numericValue = parseInt(value, 10);

													// If the input is not a valid number, do nothing
													if (isNaN(numericValue)) {
													return;
													}

													setScalableStorage(numericValue);
											}}
                      onBlur={(e) => {
                        if (!selectedPlan) return; // Ensure a plan is selected before enforcing limits
                    
                        let value = parseInt(e.target.value, 10);
                    
                        // If the input is empty, reset to the minimum value without rounding up
                        if (isNaN(value)) {
                            setScalableStorage(selectedPlan.kafkaStorageMin);
                            calculateTotalPrice(selectedPlan.kafkaStorageMin, selectedPlan, brokers); // Calculate price on blur
                            return;
                        }
                    
                        const minStorage = selectedPlan.kafkaStorageMin / 3 * brokers;
                        const maxStorage = selectedPlan.kafkaStorageMax / 3 * brokers;
                        const roundTo = 10 * brokers; // Multiply rounding factor by number of brokers
                    
                        // Enforce minimum and maximum constraints without rounding up
                        if (value <= minStorage) {
                            setScalableStorage(minStorage);
                            calculateTotalPrice(minStorage, selectedPlan, brokers); // Calculate price on blur
                        } else if (value >= maxStorage) {
                            setScalableStorage(maxStorage);
                            calculateTotalPrice(maxStorage, selectedPlan, brokers); // Calculate price on blur
                        } else {
                            // Calculate the offset from the minimum value
                            const offset = minStorage % roundTo;
                    
                            // Adjust rounding to consider the offset from the minimum value
                            value = Math.ceil((value - offset) / roundTo) * roundTo + offset;
                    
                            setScalableStorage(value);
                            calculateTotalPrice(value, selectedPlan, brokers); // Calculate price on blur
                        }
                    }}                    
										/>
									</div>
							</div>
							<div className="stepper-button-wrapper-up">
									<button 
											className="stepper-button-increment" 
											onClick={handleStorageIncrement}
											disabled={scalableStorage == selectedPlan.kafkaStorageMax /3 * brokers} // Disable if current storage is at the maximum limit
									>
											<svg className="stepper-icon-up" aria-hidden="true">
													<use href="#_sprite-arrow-up"></use>
											</svg>
									</button>
							</div>
							<div className="stepper-button-wrapper-down">
									<button 
											className="stepper-button-decrement" 
											onClick={handleStorageDecrement}
											disabled={scalableStorage == selectedPlan.kafkaStorageMin /3 * brokers} // Disable if current storage is at the minimum limit
									>
											<svg className="stepper-icon-down" aria-hidden="true">
													<use href="#_sprite-arrow-down"></use>
											</svg>
									</button>
							</div>
						</div>
						<div className='font-size-dot8'>
							<p className="font-weight-600">Storage Range</p>
							<p>{selectedPlan.kafkaStorageMin / 3 * brokers} GiB - {selectedPlan.kafkaStorageMax / 3 * brokers} GiB</p>
						</div>
					</div>

          <h5>SELECT DATABASE QUANTITY</h5>
          <input
            ref={quantityInputRef}
            type="number"
            className="input"
            value={quantity}
            onChange={handleQuantityChange}
            onBlur={handleQuantityBlur}
            min="1"
            max="1000"
          />
				</div>
      )}
			<div id="modal-footer" className="modal-footer flex flex-between flex-center">
				<div id="action-button" className="action-button">
						<button
						className="button-blue"
						onClick={handleAdd}
						disabled={!selectedPlan} // Disable the button if no plan is selected
						>
						Add Kafka
						</button>
				</div>
				<div id="price" className="price">
						<span className="dollar dollar-large">$</span>
						<span className="cost cost-large">{selectedPlan ? formatPrice(Number(totalPrice.toString().replace(/,/g, '')) * quantity) : '0'}</span>
						<span className="period period-large">/mo</span>
				</div>
			</div>
		</div>
  );
}

export default TabKafka;